import { useState, useEffect } from "react";
import { useAuth, API } from "../contexts/AuthContext";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Shield, Star, Search, Check, X, Loader2,
  Filter, Crown, AlertCircle, CheckCircle, Lock, Clock, ChevronLeft, Zap, Users
} from "lucide-react";
import useInterstitialAd from "../hooks/useInterstitialAd";
import { hapticSuccess, hapticLight } from "../utils/haptics";
import { getEffectiveTier } from "../utils/features";
import SponsorBanner from "../components/SponsorBanner";
import CricketProBanner from "../components/CricketProBanner";
import { getDisplayName, getDisplayShortName, getIPLTeamColor } from "../utils/teamNameMapping";

// Map full team names to short codes for player matching
const TEAM_SHORT_MAP = {
  "Chennai Super Kings": "CHE", "Delhi Capitals": "DEL", "Gujarat Titans": "GUJ",
  "Royal Challengers Bengaluru": "BLR", "Punjab Kings": "PUN", "Kolkata Knight Riders": "KOL",
  "Sunrisers Hyderabad": "HYD", "Rajasthan Royals": "RAJ", "Lucknow Super Giants": "LKN",
  "Mumbai Indians": "MUM"
};

const DAILY_BUDGET = 100;

const TEAM_COLORS = {
  "India": { bg: "from-orange-500 to-blue-600" },
  "Pakistan": { bg: "from-green-600 to-green-800" },
  "Australia": { bg: "from-yellow-400 to-green-600" },
  "England": { bg: "from-blue-700 to-red-600" },
  "South Africa": { bg: "from-green-500 to-yellow-500" },
  "New Zealand": { bg: "from-black to-gray-700" },
  "West Indies": { bg: "from-red-600 to-yellow-500" },
  "Sri Lanka": { bg: "from-blue-800 to-yellow-500" },
  "Zimbabwe": { bg: "from-green-600 to-yellow-400" },
  "default": { bg: "from-gray-600 to-gray-800" }
};

const getTeamColor = (team) => TEAM_COLORS[team] || TEAM_COLORS["default"];

const ROLE_REQUIREMENTS = {
  "Wicketkeeper": { min: 1, max: 4, label: "WK" },
  "Batsman": { min: 3, max: 6, label: "BAT" },
  "All-Rounder": { min: 1, max: 4, label: "AR" },
  "Bowler": { min: 3, max: 6, label: "BOWL" }
};

const ROLE_COLORS = {
  "Batsman": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Bowler": "bg-red-500/20 text-red-400 border-red-500/30",
  "All-Rounder": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "Wicketkeeper": "bg-amber-500/20 text-amber-400 border-amber-500/30"
};

const MAX_PER_TEAM = 7;

export default function DailyTeamBuilderPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user, dbUser } = useAuth();
  const { showInterstitial } = useInterstitialAd();

  const [players, setPlayers] = useState([]);
  const [matchInfo, setMatchInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [roleReqs, setRoleReqs] = useState(ROLE_REQUIREMENTS);

  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [captainId, setCaptainId] = useState(null);
  const [viceCaptainId, setViceCaptainId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");

  const [existingTeam, setExistingTeam] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTime, setLockTime] = useState(null);
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = user ? await user.getIdToken() : null;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [playersRes, teamRes] = await Promise.all([
          axios.get(`${API}/daily/players/${matchId}`),
          user ? axios.get(`${API}/daily/teams/my-team/${matchId}`, { headers }).catch(() => ({ data: { team: null } })) : Promise.resolve({ data: { team: null } }),
        ]);

        const data = playersRes.data;
        setPlayers(data.players || []);
        setMatchInfo(data.match || null);

        // Check lock status - tier-aware lock time
        if (data.match) {
          // Admin manual-open override wins: if set and still in the future, force UNLOCKED.
          const ovrRaw = data.match.daily_open_override_until;
          const ovrDt = ovrRaw ? new Date(ovrRaw) : null;
          if (ovrDt && ovrDt > new Date()) {
            setLockTime(ovrDt);
            setIsLocked(false);
          } else {
            // Parse date like "Mar 28, 2026" - remove weekday prefix and comma
            let dateStr = (data.match.date || "").replace(",", "").trim();
            dateStr = dateStr.replace(/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+/i, "").trim();
            const timeStr = data.match.time_gmt || "00:00";
            try {
              // Parse "Mar 28 2026 14:00"
              const [month, day, year] = dateStr.split(" ");
              const [hours, mins] = timeStr.split(":");
              const monthMap = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
              const matchDt = new Date(Date.UTC(parseInt(year), monthMap[month], parseInt(day), parseInt(hours), parseInt(mins)));
              // Pro Plus and Pro Max users get 25 minutes, others get 20 minutes
              const userTier = getEffectiveTier(dbUser);
              const lockMinutes = (userTier === "pro_plus" || userTier === "pro_max") ? 25 : 20;
              const lockDt = new Date(matchDt.getTime() + lockMinutes * 60 * 1000);
              setLockTime(lockDt);
              setIsLocked(new Date() > lockDt);
            } catch (e) { /* parsing failed, don't lock */ }
          }
        }

        // Load existing team
        if (teamRes.data.team) {
          const team = teamRes.data.team;
          setExistingTeam(team);
          setCaptainId(team.captain_id);
          setViceCaptainId(team.vice_captain_id);
          const teamPlayers = data.players.filter(p => team.players.includes(p.id));
          setSelectedPlayers(teamPlayers);
        }
      } catch (e) {
        setError("Failed to load match data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [matchId, user]);

  // Countdown timer - formatted as Days/Hours/Minutes/Seconds
  useEffect(() => {
    if (!lockTime || isLocked) return;
    const update = () => {
      const diff = lockTime - new Date();
      if (diff <= 0) { setIsLocked(true); setCountdown("Locked"); return; }
      const days = Math.floor(diff / 86400000);
      const hrs = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      
      // Format: show days if > 0, always show hours and minutes
      if (days > 0) {
        setCountdown(`${days}d ${hrs}h ${mins}m`);
      } else if (hrs > 0) {
        setCountdown(`${hrs}h ${mins}m ${secs}s`);
      } else {
        setCountdown(`${mins}m ${secs}s`);
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [lockTime, isLocked]);

  // Fetch tournament-aware composition rules (Women's T20 WC overrides IPL defaults)
  useEffect(() => {
    let active = true;
    axios.get(`${API}/daily-composition-rules`).then(res => {
      if (!active) return;
      const r = res.data?.rules;
      if (!r) return;
      setRoleReqs(prev => {
        const next = {};
        for (const role of Object.keys(prev)) {
          next[role] = {
            ...prev[role],
            min: r[role]?.min ?? prev[role].min,
            max: r[role]?.max ?? prev[role].max,
          };
        }
        return next;
      });
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  // Computed values
  const totalCost = selectedPlayers.reduce((sum, p) => sum + (p.credits || 8.0), 0);
  const remaining = DAILY_BUDGET - totalCost;

  const roleCounts = {};
  selectedPlayers.forEach(p => {
    const role = p.category || "Batsman";
    roleCounts[role] = (roleCounts[role] || 0) + 1;
  });

  const countryCounts = {};
  selectedPlayers.forEach(p => {
    const c = p.country || "";
    countryCounts[c] = (countryCounts[c] || 0) + 1;
  });

  const isTeamValid = selectedPlayers.length === 11 &&
    captainId && viceCaptainId && captainId !== viceCaptainId &&
    Object.entries(roleReqs).every(([role, req]) => {
      const c = roleCounts[role] || 0;
      return c >= req.min && c <= req.max;
    }) &&
    totalCost <= DAILY_BUDGET + 0.1 &&
    Object.values(countryCounts).every(c => c <= MAX_PER_TEAM);

  // Validation messages for the user
  const getValidationMessages = () => {
    const messages = [];
    if (selectedPlayers.length !== 11) {
      messages.push(`Select ${11 - selectedPlayers.length} more player${11 - selectedPlayers.length !== 1 ? 's' : ''}`);
    }
    if (selectedPlayers.length === 11 && !captainId) {
      messages.push("Select a Captain (tap player, then C)");
    }
    if (selectedPlayers.length === 11 && captainId && !viceCaptainId) {
      messages.push("Select a Vice Captain (tap player, then VC)");
    }
    if (captainId && viceCaptainId && captainId === viceCaptainId) {
      messages.push("Captain and Vice Captain must be different");
    }
    const _labels = { "Wicketkeeper": "Wicketkeeper", "Batsman": "Batsman", "Bowler": "Bowler", "All-Rounder": "All-Rounder" };
    for (const [role, req] of Object.entries(roleReqs)) {
      const c = roleCounts[role] || 0;
      if (c < req.min) {
        messages.push(`Need ${req.min - c} more ${_labels[role] || role}`);
      } else if (c > req.max) {
        messages.push(`Too many ${_labels[role] || role} (max ${req.max})`);
      }
    }
    if (totalCost > DAILY_BUDGET + 0.1) {
      messages.push(`Over budget by ${(totalCost - DAILY_BUDGET).toFixed(1)} Cr`);
    }
    return messages;
  };

  // Country filter options derived from the actually-loaded players. We filter by the
  // exact `player.country` value (full team names like "India Women" for international
  // tournaments, short codes like "MUM" for IPL) but display a short label.
  const countryShortLabels = matchInfo ? {
    [matchInfo.team1]: TEAM_SHORT_MAP[matchInfo.team1] || matchInfo.team1_short || matchInfo.team1,
    [matchInfo.team2]: TEAM_SHORT_MAP[matchInfo.team2] || matchInfo.team2_short || matchInfo.team2,
  } : {};
  const countries = [...new Set(players.map(p => p.country).filter(Boolean))];

  const filteredPlayers = players.filter(p => {
    if (searchQuery && !p.name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (roleFilter && p.category !== roleFilter) return false;
    if (countryFilter && p.country !== countryFilter) return false;
    return true;
  });

  const togglePlayer = (player) => {
    if (isLocked) return;
    const isSelected = selectedPlayers.find(p => p.id === player.id);
    if (isSelected) {
      setSelectedPlayers(prev => prev.filter(p => p.id !== player.id));
      if (captainId === player.id) setCaptainId(null);
      if (viceCaptainId === player.id) setViceCaptainId(null);
    } else {
      if (selectedPlayers.length >= 11) return;
      // Check max per team
      const cc = selectedPlayers.filter(p => p.country === player.country).length;
      if (cc >= MAX_PER_TEAM) {
        setError(`Max ${MAX_PER_TEAM} players from ${player.country}`);
        setTimeout(() => setError(""), 3000);
        return;
      }
      // Check role max (tournament-aware)
      const cat = player.category || "Batsman";
      const reqMax = roleReqs[cat]?.max;
      if (reqMax != null) {
        const rc = selectedPlayers.filter(p => (p.category || "Batsman") === cat).length;
        if (rc >= reqMax) {
          setError(`Max ${reqMax} ${cat} allowed`);
          setTimeout(() => setError(""), 3000);
          return;
        }
      }
      // Check budget
      if (totalCost + (player.credits || 8.0) > DAILY_BUDGET + 0.1) {
        setError("Over budget!");
        setTimeout(() => setError(""), 3000);
        return;
      }
      setSelectedPlayers(prev => [...prev, player]);
    }
  };

  const handleSave = async () => {
    if (!isTeamValid || isLocked || saving) return;
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };
      const body = {
        match_id: matchId,
        players: selectedPlayers.map(p => p.id),
        captain_id: captainId,
        vice_captain_id: viceCaptainId,
      };

      if (existingTeam) {
        await axios.put(`${API}/daily/teams/${matchId}`, body, { headers });
      } else {
        await axios.post(`${API}/daily/teams`, body, { headers });
      }
      setSuccess(existingTeam ? "Team updated!" : "Team saved!");
      setExistingTeam({ ...body });
      hapticSuccess();
      showInterstitial();
      setTimeout(() => setSuccess(""), 5000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save team");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-4 pb-28" data-testid="daily-team-builder">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Link to="/daily" className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors" data-testid="back-to-daily">
          <ChevronLeft className="w-5 h-5 text-slate-400" />
        </Link>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-white flex items-center gap-2" data-testid="daily-builder-title">
            <Zap className="w-5 h-5 text-amber-400" />
            {matchInfo ? `${getDisplayShortName(matchInfo.team1) || team1Short} vs ${getDisplayShortName(matchInfo.team2) || team2Short}` : "Match Day Team"}
          </h1>
          <p className="text-xs text-slate-400">{matchInfo?.date} • {matchInfo?.time_gmt} GMT</p>
        </div>
        {/* Lock status */}
        {isLocked ? (
          <span className="flex items-center gap-1 text-xs font-semibold text-red-400 bg-red-500/10 px-3 py-1.5 rounded-full" data-testid="lock-badge">
            <Lock className="w-3 h-3" /> Locked
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs font-semibold text-amber-400 bg-amber-500/10 px-3 py-1.5 rounded-full" data-testid="countdown-badge">
            <Clock className="w-3 h-3" /> {countdown}
          </span>
        )}
      </div>

      {/* Cricket Pro nudge (free users) — below the lock countdown */}
      <CricketProBanner variant="nudge" storageKey="cricket_pro_nudge_dismissed_at" />
      
      {/* Sponsor Banner - shows description */}
      {matchInfo?.sponsor && (
        <div className="mb-4">
          <SponsorBanner sponsor={matchInfo.sponsor} />
        </div>
      )}

      {/* Budget & Composition Bar */}
      <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400">Budget: <span className="text-white font-bold">{remaining.toFixed(1)} Cr</span> left</span>
          <span className="text-sm text-slate-400">Players: <span className={`font-bold ${selectedPlayers.length === 11 ? "text-emerald-400" : "text-white"}`}>{selectedPlayers.length}/11</span></span>
        </div>
        {/* Role composition */}
        <div className="flex gap-2 flex-wrap">
          {Object.entries(roleReqs).map(([role, req]) => {
            const count = roleCounts[role] || 0;
            const met = count >= req.min;
            return (
              <span key={role} className={`text-xs px-2 py-0.5 rounded border ${met ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-slate-700/50 text-slate-400 border-slate-600"}`}>
                {req.label}: {count}/{req.min}+
              </span>
            );
          })}
        </div>
        {/* Country limit warnings */}
        {Object.entries(countryCounts).map(([c, count]) => count > MAX_PER_TEAM - 1 && (
          <p key={c} className={`text-xs mt-1 ${count >= MAX_PER_TEAM ? "text-red-400" : "text-amber-400"}`}>
            {c}: {count}/{MAX_PER_TEAM} max
          </p>
        ))}
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3 flex items-center gap-2" data-testid="error-message">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mb-3 flex items-center gap-2" data-testid="success-message">
          <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
          <p className="text-sm text-emerald-400">{success}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            placeholder="Search players..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-amber-500/50 outline-none"
            data-testid="player-search"
          />
        </div>
        {/* Country filter */}
        {countries.map(c => (
          <button
            key={c}
            onClick={() => setCountryFilter(countryFilter === c ? "" : c)}
            className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${countryFilter === c ? "bg-amber-500/20 border-amber-500/40 text-amber-400" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"}`}
            data-testid={`filter-country-${c}`}
          >
            {countryShortLabels[c] || c}
          </button>
        ))}
      </div>

      {/* Role filter tabs */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto">
        <button
          onClick={() => setRoleFilter("")}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold shrink-0 ${!roleFilter ? "bg-amber-500/20 text-amber-400" : "bg-slate-800 text-slate-400"}`}
          data-testid="filter-role-all"
        >
          All
        </button>
        {Object.entries(ROLE_REQUIREMENTS).map(([role, req]) => (
          <button
            key={role}
            onClick={() => setRoleFilter(roleFilter === role ? "" : role)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold shrink-0 ${roleFilter === role ? "bg-amber-500/20 text-amber-400" : "bg-slate-800 text-slate-400"}`}
            data-testid={`filter-role-${req.label}`}
          >
            {req.label}
          </button>
        ))}
      </div>

      {/* Player List */}
      <div className="space-y-2 mb-4" data-testid="player-list">
        {filteredPlayers.map(player => {
          const isSelected = selectedPlayers.find(p => p.id === player.id);
          const isCaptain = captainId === player.id;
          const isVC = viceCaptainId === player.id;
          const teamColor = getTeamColor(player.country);

          return (
            <div
              key={player.id}
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                isSelected
                  ? "bg-amber-500/5 border-amber-500/30"
                  : "bg-slate-800/50 border-slate-700/50 hover:border-slate-600"
              } ${isLocked ? "opacity-70 cursor-default" : ""}`}
              onClick={() => togglePlayer(player)}
              data-testid={`player-card-${player.id}`}
            >
              {/* Avatar */}
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${teamColor.bg} flex items-center justify-center shrink-0`}>
                <span className="text-white text-xs font-bold">{player.name?.charAt(0)}</span>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{player.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-xs px-1.5 py-0.5 rounded border ${ROLE_COLORS[player.category] || "bg-slate-700 text-slate-300"}`}>
                    {ROLE_REQUIREMENTS[player.category]?.label || player.category}
                  </span>
                  <span className="text-xs text-slate-500">{player.country}</span>
                </div>
              </div>

              {/* Credits */}
              <span className="text-sm font-bold text-amber-400 shrink-0">{player.credits} Cr</span>

              {/* Captain/VC badges or selection indicator */}
              {isSelected && !isLocked && (
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); setCaptainId(isCaptain ? null : player.id); if (viceCaptainId === player.id) setViceCaptainId(null); hapticLight(); }}
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border transition-colors min-w-[44px] min-h-[44px] ${isCaptain ? "bg-amber-500 text-black border-amber-400" : "bg-slate-700 text-slate-400 border-slate-600 hover:border-amber-500/50"}`}
                    data-testid={`captain-btn-${player.id}`}
                    title="Captain (2x points)"
                  >
                    C
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setViceCaptainId(isVC ? null : player.id); if (captainId === player.id) setCaptainId(null); hapticLight(); }}
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border transition-colors min-w-[44px] min-h-[44px] ${isVC ? "bg-cyan-500 text-black border-cyan-400" : "bg-slate-700 text-slate-400 border-slate-600 hover:border-cyan-500/50"}`}
                    data-testid={`vc-btn-${player.id}`}
                    title="Vice Captain (1.5x points)"
                  >
                    VC
                  </button>
                </div>
              )}
              {isSelected && isLocked && (
                <div className="flex gap-1 shrink-0">
                  {isCaptain && <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-amber-500 text-black">C</span>}
                  {isVC && <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-cyan-500 text-black">VC</span>}
                </div>
              )}

              {/* Selection indicator */}
              {!isLocked && (
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${isSelected ? "bg-amber-500 border-amber-400" : "border-slate-600"}`}>
                  {isSelected && <Check className="w-3.5 h-3.5 text-black" />}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Fixed Save Button */}
      {!isLocked && (
        <div className="fixed left-0 right-0 px-4 z-30" style={{ bottom: "calc(5rem + env(safe-area-inset-bottom, 0px))" }}>
          <div className="max-w-4xl mx-auto">
            {/* Validation Messages */}
            {!isTeamValid && getValidationMessages().length > 0 && (
              <div className="mb-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 text-xs text-center">{getValidationMessages()[0]}</p>
              </div>
            )}
            <button
              onClick={handleSave}
              disabled={!isTeamValid || saving}
              className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all ${
                isTeamValid
                  ? "bg-amber-500 hover:bg-amber-400 text-black shadow-lg shadow-amber-500/20"
                  : "bg-slate-700 text-slate-500 cursor-not-allowed"
              }`}
              data-testid="save-daily-team-btn"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin inline" />
              ) : existingTeam ? (
                `Update Team${countdown ? ` (locks in ${countdown})` : ""}`
              ) : (
                `Save Team${countdown ? ` (locks in ${countdown})` : ""}`
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
